import {
    Chart as ChartJS,
    Title,
    Tooltip,
    Legend,
    LineElement,
    LineController,
    PointElement,
    CategoryScale,
    LinearScale,
    Filler
} from 'chart.js'

// Custom spline curve calculation (Chart.helpers.splineCurve was removed in v3)
function splineCurve(p0, p1, p2, tension) {
    const d01 = Math.sqrt(Math.pow(p1.x - p0.x, 2) + Math.pow(p1.y - p0.y, 2))
    const d12 = Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2))
    const fa = tension * d01 / (d01 + d12 || 1)
    const fb = tension - fa
    return {
        previous: {
            x: p1.x - fa * (p2.x - p0.x),
            y: p1.y - fa * (p2.y - p0.y)
        },
        next: {
            x: p1.x + fb * (p2.x - p0.x),
            y: p1.y + fb * (p2.y - p0.y)
        }
    }
}

// Custom controller that extends lines with bezier curves at edges
// Ported from Chart.js v2 logic
class LineWithOffsetController extends LineController {
    static id = 'lineWithOffset'
    static defaults = LineController.defaults

    draw() {
        // Call parent draw first
        super.draw()

        // Draw custom bezier extensions
        // Only draw extension if data point is near the chart edge
        const ctx = this.chart.ctx

        const meta = this.getMeta()
        const points = meta.data

        if (!points || points.length < 1) return

        // Get stroke color from dataset
        const dataset = this.chart.data.datasets[meta.index]
        if (!dataset) return

        // Save context state and set styles
        ctx.save()
        ctx.lineWidth = dataset.borderWidth || 3
        ctx.strokeStyle = dataset.borderColor || '#000'

        const chartLeft = this.chart.chartArea.left
        const chartRight = this.chart.chartArea.right

        // Find first and last points with actual data (non-null, non-zero)
        // Check the raw dataset data, not just point coordinates
        let firstValidPoint = null
        let firstValidIndex = -1
        let lastValidPoint = null
        let lastValidIndex = -1

        const rawData = dataset.data || []
        for (let i = 0; i < points.length; i++) {
            const point = points[i]
            const dataValue = rawData[i]
            // Check if point has valid coordinates AND has data (null = no data, 0 = valid data)
            const hasData = dataValue !== null && dataValue !== undefined
            if (point && isFinite(point.x) && isFinite(point.y) && hasData) {
                if (firstValidPoint === null) {
                    firstValidPoint = point
                    firstValidIndex = i
                }
                lastValidPoint = point
                lastValidIndex = i
            }
        }

        if (!firstValidPoint || !lastValidPoint) return

        // Calculate point spacing to determine if we're near the edges
        const chartWidth = chartRight - chartLeft
        const pointSpacing = points.length > 1 ? chartWidth / (points.length - 1) : chartWidth

        // Check if first/last valid points are near chart edges (within 1.5x point spacing)
        const distanceFromLeft = firstValidPoint.x - chartLeft
        const distanceFromRight = chartRight - lastValidPoint.x
        const drawLeft = firstValidIndex === 0 || distanceFromLeft <= pointSpacing * 0.5
        const drawRight = distanceFromRight <= pointSpacing * 1.5

        // LEFT EXTENSION - if first valid point is at or near the left edge
        if (drawLeft) {
            const distanceFromLeft = firstValidPoint.x - chartLeft
            ctx.beginPath()

            const widthLeft = distanceFromLeft + 5

            let leftPosition = {
                start: {
                    x: firstValidPoint.x,
                    y: firstValidPoint.y
                },
                end: {
                    x: firstValidPoint.x - widthLeft,
                    y: firstValidPoint.y
                }
            }

            // Get next valid point for curve calculation
            const nextPoint = firstValidIndex + 1 < points.length ? points[firstValidIndex + 1] : null
            if (nextPoint && isFinite(nextPoint.x) && isFinite(nextPoint.y)) {
                const startCurve = splineCurve(
                    { x: nextPoint.x, y: nextPoint.y },
                    leftPosition.start,
                    leftPosition.end,
                    0.4
                )
                leftPosition.end.y = startCurve.next.y
            }

            ctx.moveTo(leftPosition.end.x, leftPosition.end.y)
            ctx.bezierCurveTo(
                leftPosition.end.x + widthLeft / 2,
                leftPosition.end.y,
                leftPosition.start.x,
                leftPosition.start.y,
                leftPosition.start.x,
                leftPosition.start.y
            )

            ctx.stroke()
        }

        // RIGHT EXTENSION - if last valid point is near the right edge
        if (drawRight) {
            ctx.beginPath()

            const widthRight = distanceFromRight + 5

            let rightPosition = {
                start: {
                    x: lastValidPoint.x,
                    y: lastValidPoint.y
                },
                end: {
                    x: lastValidPoint.x + widthRight,
                    y: lastValidPoint.y
                }
            }

            // Get previous valid point for curve calculation
            const prevPoint = lastValidIndex > 0 ? points[lastValidIndex - 1] : null
            if (prevPoint && isFinite(prevPoint.x) && isFinite(prevPoint.y)) {
                const endCurve = splineCurve(
                    { x: prevPoint.x, y: prevPoint.y },
                    rightPosition.start,
                    rightPosition.end,
                    0.4
                )
                rightPosition.end.y = endCurve.next.y
            }

            ctx.moveTo(rightPosition.start.x, rightPosition.start.y)
            ctx.bezierCurveTo(
                rightPosition.start.x,
                rightPosition.start.y,
                rightPosition.end.x - widthRight / 2,
                rightPosition.end.y,
                rightPosition.end.x,
                rightPosition.end.y
            )

            ctx.stroke()
        }

        // Restore context state
        ctx.restore()
    }
}

// Register all required components
ChartJS.register(
    Title,
    Tooltip,
    Legend,
    LineElement,
    LineController,
    PointElement,
    CategoryScale,
    LinearScale,
    Filler,
    LineWithOffsetController
)

export default {
    name: 'LineChartExtended',
    props: {
        chartData: {
            type: Object,
            required: false,
            default: () => ({ labels: [], datasets: [] })
        },
        options: {
            type: Object,
            required: false,
            default: () => ({})
        },
        height: {
            type: [Number, String],
            default: null
        }
    },
    data() {
        return {
            chart: null,
            internalData: null,
            internalOptions: null
        }
    },
    computed: {
        activeData() {
            return this.internalData || this.chartData
        },
        activeOptions() {
            return this.internalOptions || this.options
        }
    },
    watch: {
        chartData: {
            deep: true,
            handler(newData) {
                this.updateChart(newData, this.activeOptions)
            }
        },
        options: {
            deep: true,
            handler(newOptions) {
                this.updateChart(this.activeData, newOptions)
            }
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.createChart()
        })
    },
    beforeDestroy() {
        if (this.chart) {
            this.chart.destroy()
            this.chart = null
        }
    },
    render(h) {
        const containerStyle = {
            position: 'relative',
            width: '100%',
            height: this.height
                ? (typeof this.height === 'number' ? `${this.height}px` : this.height)
                : '100%'
        }
        return h('div', { style: containerStyle }, [
            h('canvas', { ref: 'canvas' })
        ])
    },
    methods: {
        createChart() {
            if (!this.$refs.canvas) return

            const ctx = this.$refs.canvas.getContext('2d')
            const data = this.addDefaultTension(this.activeData)

            const options = {
                responsive: true,
                maintainAspectRatio: false,
                ...this.activeOptions
            }

            this.chart = new ChartJS(ctx, {
                type: 'lineWithOffset',
                data: data,
                options: options
            })
        },
        updateChart(data, options) {
            if (this.chart) {
                const processedData = this.addDefaultTension(data)
                const mergedOptions = {
                    responsive: true,
                    maintainAspectRatio: false,
                    ...options
                }

                this.chart.data.labels = processedData.labels
                this.chart.data.datasets = processedData.datasets
                this.chart.options = mergedOptions
                this.chart.update()
            }
        },
        addDefaultTension(data) {
            if (!data || !data.datasets) return data

            return {
                ...data,
                datasets: data.datasets.map(dataset => ({
                    tension: 0.4,
                    ...dataset
                }))
            }
        },
        renderChart(data, options) {
            this.internalData = data
            this.internalOptions = options

            if (this.chart) {
                this.chart.destroy()
                this.chart = null
            }
            this.$nextTick(() => this.createChart())
        }
    }
}
